"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "@/components/header";
import Footer from "@/components/footer";
import { CreditCard } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import {
  FeatureItem,
  type FeatureItemAddress,
} from "@/components/ui/feature-item";
import { InfoBox } from "@/components/ui/info-box";
import { containerVariants, itemVariants } from "@/lib/animation-variants";

interface ScheduleEvent {
  time: string;
  title: string;
  subtitle?: string;
  description: string;
  address: FeatureItemAddress;
  highlight?: boolean;
  note?: string;
  dressCode?: string;
}

interface ScheduleDay {
  day: string;
  date: string;
  events: ScheduleEvent[];
}

const scheduleData: ScheduleDay[] = [
  {
    day: "Donnerstag",
    date: "11.06.",
    events: [
      {
        time: "18:00 Uhr",
        title: "Begrüßungsabend und Eröffnung",
        description: "Gothenhaus",
        address: {
          line1: "Wilhelm-Busch-Straße 24",
          line2: "30167 Hannover",
        },
        highlight: true,
        note: "Gemütliches Beisammensein zum Auftakt des Stiftungsfests",
      },
    ],
  },
  {
    day: "Freitag",
    date: "12.06.",
    events: [
      {
        time: "18:00 Uhr",
        title: "Chargenessen",
        description: "Gothenhaus",
        address: {
          line1: "Wilhelm-Busch-Straße 24",
          line2: "30167 Hannover",
        },
        note: "Transfer zum Kommers wird organisiert",
      },
      {
        time: "20:00 Uhr",
        title: "Festkommers",
        subtitle: "Einlass ab 19:30 Uhr · Vorherige Anmeldung erforderlich · Ausverkauft",
        description: "Altes Rathaus",
        address: {
          line1: "Karmarschstraße 42",
          line2: "30159 Hannover",
        },
        highlight: true,
        dressCode: "Wichs / Vollcouleur / Abendgarderobe – keine Sneaker",
      },
      {
        time: "00:00 Uhr",
        title: "Fidulitas",
        description: "Gothenhaus",
        address: {
          line1: "Wilhelm-Busch-Straße 24",
          line2: "30167 Hannover",
        },
      },
    ],
  },
  {
    day: "Samstag",
    date: "13.06.",
    events: [
      {
        time: "11:00 Uhr",
        title: "Convente",
        description: "Gothenhaus",
        address: {
          line1: "Wilhelm-Busch-Straße 24",
          line2: "30167 Hannover",
        },
      },
      {
        time: "10:00 Uhr",
        title: "Damen- und Gästeprogramm",
        subtitle: "Brunch (Selbstzahler) mit anschließendem Minigolf in den Georgengärten · Vorherige Anmeldung erforderlich",
        description: "Treffpunkt: Gothenhaus",
        address: {
          line1: "Wilhelm-Busch-Straße 24",
          line2: "30167 Hannover",
        },
      },
      {
        time: "19:00 Uhr",
        title: "Festball",
        subtitle: "Einlass ab 18:30 Uhr · Vorherige Anmeldung erforderlich · Ausverkauft",
        description: "Hotel Dormero",
        address: {
          line1: "Hildesheimer Str. 34-38",
          line2: "30169 Hannover",
        },
        highlight: true,
        dressCode: "Abendgarderobe – keine Sneaker",
      },
    ],
  },
  {
    day: "Sonntag",
    date: "14.06.",
    events: [
      {
        time: "10:00 Uhr",
        title: "Hl. Messe",
        description: "St. Clemens Basilika",
        address: {
          line1: "Platz an der Basilika 1",
          line2: "30169 Hannover",
        },
      },
      {
        time: "12:00 Uhr",
        title: "Feierlicher Ausklang",
        description: "Gothenhaus",
        address: {
          line1: "Wilhelm-Busch-Straße 24",
          line2: "30167 Hannover",
        },
        highlight: true,
        note: "Gemütlicher Ausklang mit Frühschoppen",
      },
    ],
  },
];

export default function ProgrammPage() {
  const [activeDay, setActiveDay] = useState(0);

  useEffect(() => {
    const now = new Date();
    let initial = 0;
    for (let i = 0; i < scheduleData.length; i++) {
      const [day, month] = scheduleData[i].date
        .replace(/\.$/, "")
        .split(".")
        .map(Number);
      const eventDate = new Date(2026, month - 1, day);
      if (now >= eventDate) initial = i;
    }
    setActiveDay(initial);
  }, []);
  const highlightEvents = scheduleData[activeDay].events.filter(
    (e) => e.highlight,
  );
  const secondaryEvents = scheduleData[activeDay].events.filter(
    (e) => !e.highlight,
  );

  return (
    <div className="flex min-h-screen flex-col bg-gothia-green">
      <Header />

      <main className="flex-1">
        <section className="relative px-4 pb-10 pt-6 sm:px-6 sm:pb-16 sm:pt-10 lg:px-8">
          {/* Wappen watermark */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundColor: "#BDA44E",
              WebkitMask: "url(/Wappen.svg) no-repeat center 20%/80%",
              mask: "url(/Wappen.svg) no-repeat center 20%/80%",
            }}
          />

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="relative z-10 mx-auto max-w-4xl">
            <motion.h1
              variants={itemVariants}
              className="mb-6 text-center font-heading text-4xl text-gothia-gold sm:mb-3 sm:text-6xl">
              Programm
            </motion.h1>

            <motion.p
              variants={itemVariants}
              className="mb-10 hidden text-center font-body text-white/70 sm:block">
              11. – 14. Juni 2026 in Hannover
            </motion.p>

            {/* Day Tabs */}
            <motion.div variants={itemVariants} className="mb-4 sm:mb-12">
              <div className="rounded-2xl border border-gothia-gold/20 bg-gothia-green p-1.5 sm:p-2">
                <div className="flex flex-col gap-1 sm:flex-row sm:gap-2">
                  {scheduleData.map((day, index) => (
                    <button
                      key={day.day}
                      onClick={() => setActiveDay(index)}
                      className={`flex w-full items-baseline justify-between gap-3 rounded-lg px-3 py-2 text-left font-body transition-all duration-300 sm:min-w-[5.5rem] sm:flex-1 sm:flex-col sm:items-stretch sm:justify-center sm:gap-0 sm:rounded-xl sm:px-4 sm:py-4 sm:text-center ${
                        activeDay === index
                          ? "bg-gothia-cream text-gothia-green shadow-lg"
                          : "text-white/70 hover:bg-white/5 hover:text-white"
                      }`}>
                      <span className="text-sm font-semibold sm:order-2 sm:mt-1 sm:block sm:text-sm md:text-base">
                        {day.day}
                      </span>
                      <span className="text-xs font-semibold uppercase tracking-wider opacity-70 sm:order-1 sm:block">
                        {day.date}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeDay}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.25 }}
                className="space-y-8">
                {/* Highlight Events */}
                {highlightEvents.map((event, i) => (
                  <motion.div
                    key={`highlight-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1, duration: 0.3 }}>
                    <Card
                      variant="feature"
                      className="ring-2 ring-gothia-gold/40">
                      <CardContent>
                        <FeatureItem
                          time={event.time}
                          title={event.title}
                          subtitle={event.subtitle}
                          description={event.description}
                          address={event.address}
                          isLast={true}
                        />
                        {(event.dressCode || event.note) && (
                          <div className="mt-4 border-t border-gothia-gold/20 pt-4">
                            {event.dressCode && (
                              <p className="font-body text-sm text-gray-500">
                                <span className="font-semibold text-gray-600">Dresscode:</span>{" "}
                                {event.dressCode}
                              </p>
                            )}
                            {event.note && (
                              <p className={`font-body text-sm italic text-gray-500 ${event.dressCode ? "mt-2" : ""}`}>
                                {event.note}
                              </p>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}

                {/* Gold divider */}
                {secondaryEvents.length > 0 && highlightEvents.length > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="h-px flex-1 bg-gothia-gold/20" />
                    <div className="h-1.5 w-1.5 rotate-45 bg-gothia-gold/40" />
                    <div className="h-px flex-1 bg-gothia-gold/20" />
                  </div>
                )}

                {/* Secondary Events */}
                {secondaryEvents.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-body text-sm font-semibold uppercase tracking-wider text-white/50">
                      Weitere Programmpunkte
                    </h3>
                    {secondaryEvents.map((event, i) => (
                      <motion.div
                        key={`secondary-${i}`}
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          delay: (highlightEvents.length + i) * 0.1,
                          duration: 0.3,
                        }}>
                        <Card variant="feature" className="h-full">
                          <CardContent className="h-full">
                            <div className="flex items-start gap-4">
                              <div className="w-28 shrink-0 font-body text-sm font-semibold text-gothia-green">
                                {event.time}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="font-body font-semibold text-gothia-green">
                                  {event.title}
                                </div>
                                {event.subtitle && (
                                  <div className="mt-0.5 font-body text-sm text-gray-600">
                                    {event.subtitle}
                                  </div>
                                )}
                                <div className="mt-0.5 font-body text-sm text-gray-500">
                                  {event.description}
                                </div>
                                {event.address && (event.address.line1 || event.address.line2) && (
                                  <div className="mt-0.5 font-body text-xs text-gray-400">
                                    {event.address.line1}
                                    {event.address.line1 && event.address.line2 && ", "}
                                    {event.address.line2}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <motion.div variants={itemVariants} className="mt-12">
              <InfoBox
                variant="info"
                icon={<CreditCard className="h-5 w-5" />}
                title="Bezahlung vor Ort">
                Während des gesamten Stiftungsfestes sind sowohl Karten- als auch
                Barzahlung möglich – dies gilt an allen Veranstaltungsorten, also
                im Gothenhaus, im Alten Rathaus und im Hotel Dormero. Wir bitten
                euch, nach Möglichkeit mit Karte zu zahlen.
              </InfoBox>
            </motion.div>
          </motion.div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
